// Small display helpers shared by components.

export function initials(name) {
  const words = (name || '?').trim().split(/\s+/).filter(Boolean)
  const letters = words.length > 1 ? words[0][0] + words[words.length - 1][0] : (words[0] || '?').slice(0, 2)
  return letters.toUpperCase()
}

/** Stable 1..8 bucket for avatar colors. */
export function colorIndex(seed) {
  let hash = 0
  for (const char of seed || '') hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return (hash % 8) + 1
}

/** "Chrome on macOS" from a user agent string. */
export function describeAgent(agent) {
  const ua = agent || ''
  const browser =
    (/Edg\//.test(ua) && 'Edge') ||
    (/Firefox\//.test(ua) && 'Firefox') ||
    (/Chrome\//.test(ua) && 'Chrome') ||
    (/Safari\//.test(ua) && 'Safari') ||
    (/curl\//i.test(ua) && 'curl') ||
    null
  const os =
    (/iPhone|iPad/.test(ua) && 'iOS') ||
    (/Android/.test(ua) && 'Android') ||
    (/Mac OS X/.test(ua) && 'macOS') ||
    (/Windows/.test(ua) && 'Windows') ||
    (/Linux/.test(ua) && 'Linux') ||
    null
  if (browser && os) return `${browser} on ${os}`
  return browser || os || 'Unknown device'
}

export function isMobileAgent(agent) {
  return /iPhone|iPad|Android|Mobile/.test(agent || '')
}

const UNITS = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
]

/** "5 minutes ago", "in 3 days", "just now". */
export function relativeTime(date, now = new Date()) {
  const seconds = Math.round((new Date(date) - now) / 1000)
  const abs = Math.abs(seconds)
  if (abs < 45) return 'just now'
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  for (const [unit, size] of UNITS) {
    if (abs >= size || unit === 'minute') return format.format(Math.round(seconds / size), unit)
  }
  return 'just now'
}

export function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
}

/** Doc text to safe HTML: escaped, `code` spans, blank lines become paragraphs. */
export function docHtml(text) {
  return String(text || '')
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escapeHtml(block).replace(/`([^`]+)`/g, '<code>$1</code>')}</p>`)
    .join('')
}

/** "820 B", "14 KB", "1.2 MB". */
export function formatBytes(bytes) {
  const value = Number(bytes) || 0
  if (value < 1024) return `${value} B`
  const units = ['KB', 'MB', 'GB']
  let size = value / 1024
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit++
  }
  return `${size < 10 ? Math.round(size * 10) / 10 : Math.round(size)} ${units[unit]}`
}

// Matched in order against the content type, then the file extension.
const FILE_ICONS = [
  { type: /^image\//, ext: /\.(png|jpe?g|gif|webp|svg|heic|bmp)$/, icon: 'file-image' },
  { type: /pdf$/, ext: /\.pdf$/, icon: 'file-pdf' },
  { type: /(zip|compressed|tar|gzip|rar|7z)/, ext: /\.(zip|tar|gz|tgz|rar|7z)$/, icon: 'file-zip' },
  { type: /^audio\//, ext: /\.(mp3|wav|m4a|ogg|flac)$/, icon: 'file-audio' },
  { type: /^video\//, ext: /\.(mp4|mov|webm|mkv|avi)$/, icon: 'file-video' },
  { type: /(spreadsheet|excel|csv)/, ext: /\.(xlsx?|csv|ods|numbers)$/, icon: 'file-xls' },
  { type: /(wordprocessing|msword|opendocument\.text|rtf)/, ext: /\.(docx?|odt|rtf|pages)$/, icon: 'file-doc' },
  { type: /(presentation|powerpoint)/, ext: /\.(pptx?|odp|key)$/, icon: 'file-ppt' },
  { type: /(text\/calendar|application\/ics)/, ext: /\.ics$/, icon: 'calendar-blank' },
  { type: /^text\//, ext: /\.(txt|md|log|json|xml|ya?ml)$/, icon: 'file-text' },
]
const GENERIC_FILE = { icon: 'file' }

/** Phosphor icon name for an attachment. */
export function fileIcon(contentType, filename) {
  const type = String(contentType || '').toLowerCase()
  const name = String(filename || '').toLowerCase()
  const byType = type && type !== 'application/octet-stream' && FILE_ICONS.find((entry) => entry.type.test(type))
  return (byType || FILE_ICONS.find((entry) => entry.ext.test(name)) || GENERIC_FILE).icon
}
